import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Spinner,
  ModalFooter,
  Center,
  SimpleGrid,
  ModalCloseButton,
  Text,
} from "@chakra-ui/react";
import useSWR from "swr";
import axios from "axios";
import { useTranslation } from "next-i18next";
import useInfiniteScroll from "react-infinite-scroll-hook";

import { useCloud } from "@/hooks/useCloud";
import AlertMessage from "@/components/common/AlertMessage";
import { useToastPromise } from "@/hooks/useToast";
import { FolderRow } from "./Folder";
import { FileRow } from "./File";
import { Folder } from "@/types/cloud";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedFolders: number[];
  selectedFiles: number[];
  setSelectedFolders: (folders: number[]) => void;
  setSelectedFiles: (files: number[]) => void;
  refreshData: () => void;
};

const ChooseFolderToMoveModal = ({
  isOpen,
  onClose,
  selectedFiles,
  selectedFolders,
  setSelectedFolders,
  setSelectedFiles,
  refreshData,
}: Props): React.ReactElement => {
  const { t } = useTranslation("common");
  const { data: rootFolder, error } = useSWR("/cloud/root");
  const [currentDir, setCurrentDir] = useState<Folder>(null);
  const cloudData = useCloud(currentDir?.id);
  const toast = useToastPromise();
  const [moveLoading, setMoveLoading] = useState(false);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    hasNextPage: cloudData?.hasNextPage,
    disabled: !!cloudData?.error,
    loading: cloudData?.isLoading,
    onLoadMore: cloudData?.loadMore,
    rootMargin: "0px 0px 50px 0px",
  });

  useEffect(() => {
    rootFolder && setCurrentDir(rootFolder);
  }, [rootFolder]);

  const onFolderClick = (folder: Folder) => {
    setCurrentDir(folder);
  };

  const onMove = () => {
    setMoveLoading(true);

    toast.promise(
      axios
        .post("/cloud/move", {
          folders: selectedFolders,
          files: selectedFiles,
          parentFolderId: currentDir?.id,
        })
        .then(() => {
          setSelectedFolders([]);
          setSelectedFiles([]);
          refreshData();
          onClose();
        })
        .finally(() => setMoveLoading(false)),
      {
        success: t("cloud.DnD.successSelected"),
        pending: t("cloud.DnD.pendingSelected"),
        error: t("cloud.DnD.errorSelected"),
      }
    );
  };

  return (
    <Modal
      motionPreset="slideInBottom"
      isOpen={isOpen}
      onClose={onClose}
      preserveScrollBarGap
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        w="700px"
        h="600px"
        maxH="100%"
        maxW="100%"
        overflow="hidden"
      >
        <ModalHeader>
          {t("cloud.chooseFolder")}
          {currentDir && currentDir?.id !== rootFolder?.id && (
            <Text mt={2} color="GrayText">
              {t("cloud.choosedFolder", {
                name: currentDir?.name,
              })}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />

        {(error || cloudData?.error) && (
          <AlertMessage
            status="error"
            message={t("errorKeys.getDataError")}
            m={2}
          />
        )}

        <ModalBody ref={rootRef}>
          <SimpleGrid
            columns={{ base: 2, md: 1 }}
            spacing={{ base: 6, md: 0 }}
            flex={{ base: "none", md: 1 }}
          >
            {!cloudData && <Spinner />}

            {cloudData?.parentFolderId && (
              <FolderRow
                folder={{
                  id: cloudData?.parentFolderId,
                  name: "..",
                  slug: cloudData?.parentFolder?.slug,
                  path: cloudData?.parentFolder?.path,
                  files: [],
                }}
                onClick={() => onFolderClick(cloudData?.parentFolder)}
                hideCheckbox={true}
                hideDetails={true}
                d={{ base: "none", md: "flex" }}
              />
            )}

            {cloudData?.folders?.length > 0 &&
              cloudData?.folders.map((folder) => (
                <FolderRow
                  key={`folder-${folder?.id}`}
                  folder={folder}
                  onClick={() => onFolderClick(folder)}
                  filesCount={folder?.files?.length || 0}
                  d={{ base: "none", md: "flex" }}
                  hideCheckbox
                />
              ))}

            {cloudData?.files?.length > 0 &&
              cloudData?.files?.map((file) => (
                <FileRow
                  key={`files-${file?.id}`}
                  file={file}
                  onClick={() => null}
                  hideDetails={true}
                  d={{ base: "none", md: "flex" }}
                  opacity={0.5}
                  cursor="default"
                />
              ))}

            {(cloudData?.isLoading || cloudData?.hasNextPage) && (
              <Center ref={sentryRef} mt="2">
                <Spinner />
              </Center>
            )}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button isLoading={moveLoading} onClick={onMove}>
            {t("move")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChooseFolderToMoveModal;
